/**
 * Grid view: one uPlot panel per ship.
 *
 * Deliberately does **not** reuse `renderLine`/`renderScatter`. Those write
 * through `rebuildUplot` into the single `runtime.instance`/`runtime.rootEl`
 * pair, and every hook downstream - tooltip, drag-zoom, leg and changeover
 * overlays, image export - reads that singleton. Rewriting all of it to be
 * instance-keyed would put the working single-chart path at risk for no gain,
 * so grid keeps its own instances in `runtime.gridInstances` and shares only
 * the pure helpers.
 *
 * Panels need no union merge: each one draws its own ship's response at that
 * ship's own timestamps, which is the whole reason grid stays readable where an
 * overlay of six ships does not.
 *
 * **Shared Y** is the default and the point of the view. With independent
 * scales two ships whose values differ tenfold draw as the same shape in the
 * same box, and nothing on screen says otherwise.
 *
 * **Zoom stays linked for free.** A drag writes the new window into the
 * `startDate`/`endDate` atoms and schedules a replot, exactly as the single
 * chart does; every panel is then rebuilt from that one shared range, so they
 * cannot drift apart.
 */

import uPlot from 'uplot';
import { plotState } from '../../../state/plot-state.ts';
import { buildSeriesColorer, colorScaleGradientCss, fitColorFor } from '../plot-constants.ts';
import { runtime, destroyGrid } from '../plot-runtime.ts';
import { chartReady, colorLegend, plotFocusShip, plottedSource } from '../plot-ui-state.ts';
import { sourceHasHistory } from '../../../state/data-sources.ts';
import { themeColors, type ThemeColors } from './chart-dom.ts';
import { attachLegendTools } from './legend-tools.ts';
import { buildHeatmapOpts } from './render-heatmap.ts';
import { buildCorrelationOpts, correlationLegend } from './render-correlation.ts';
import { buildPieOpts, pieLegend } from './render-pie.ts';
import { buildSankeyOpts } from './render-sankey.ts';
import { boxLegend, boxYRange, buildBoxOpts } from './render-box.ts';
import { buildNumberOpts } from './render-number.ts';
import type { NumberResponse } from '../../../api/types.ts';
import {
  applyChartFontVar,
  dedupeByX,
  computeGapThreshold,
  injectGaps,
  bridgeOwnGaps,
  applyHoverFocus,
  buildLegendOpts,
  buildXAxis,
  buildYAxis,
  collectThresholds,
  followLiveWindow,
  yAxisTitle,
  pinLiveWindow,
  utcTzDate,
} from './uplot-helpers.ts';
import { onXScaleChanged } from '../zoom.ts';
import { onPeriodXScaleChanged } from '../zoom.ts';
import { updateTooltip } from './tooltip.ts';
import { withAlpha } from './format.ts';
import { shipLabel } from '../../../utils/ships.ts';
import {
  drawLegBands,
  drawChangeoverBands,
  drawColorLegendBox,
  drawDiagonal,
  drawThresholds,
} from './overlays.ts';
import {
  buildColorBuckets,
  colorPlanFor,
  drawBucketedScatterSeries,
  drawColoredLineSegments,
  numericColorDomain,
  planColors,
  pointBuckets,
  type ColorPlan,
} from './color-plan.ts';
import type { ShipPlotResult } from '../multi-ship-plot.ts';
import type { PlotPanelResult } from '../period-plot.ts';
import type {
  CorrelationResponse,
  PieResponse,
  SankeyResponse,
  BoxResponse,
  HeatmapResponse,
  PlotResponse,
  PlotSeries,
} from '../../../api/types.ts';
import {
  expandedPeriodId,
  formatPeriodDuration,
  periodComparison,
  periodDurationSeconds,
  resolveComparisonPeriods,
  utcEpochSeconds,
} from '../../../state/period-comparison.ts';

/** Min panel width before the grid drops to fewer columns. */
const MIN_PANEL_WIDTH = 300;
/** Floor for the plot area itself. Below this uPlot has no room for its axes. */
const PANEL_MIN_HEIGHT = 90;
/** Width at which a third column still leaves each panel readable. */
const THREE_COL_WIDTH = 1000;
const GRID_GAP = 10;

/** Most panels the "Panels per row" setting will put on one row. */
export const GRID_COLUMNS_MAX = 4;

/**
 * Columns for `count` panels in a host `width` px wide.
 *
 * Automatically: three at most on a wide screen, two on a normal one, one when
 * even two would be narrower than a panel can usefully be. Capped at three
 * because a fourth column puts every panel under ~300px, where the axis labels
 * start colliding.
 *
 * `chosen` is the "Panels per row" setting, and it overrides all of that - if
 * somebody asks for four on a laptop they get four narrow panels, which is
 * their call to make. It is still capped at the number of panels there are, so
 * three ships never leave a fourth column empty.
 */
export function gridColumns(
  width: number,
  count: number,
  periodComparison = false,
  chosen: number | null = null,
): number {
  if (count <= 1) return 1;
  if (chosen) return Math.max(1, Math.min(count, Math.round(chosen), GRID_COLUMNS_MAX));
  if (periodComparison) return width >= 730 ? 2 : 1;
  const maxCols = width >= THREE_COL_WIDTH ? 3 : 2;
  const fits = Math.floor((width + GRID_GAP) / (MIN_PANEL_WIDTH + GRID_GAP));
  return Math.max(1, Math.min(count, maxCols, Math.max(1, fits)));
}

/** Ships that returned drawable line/scatter data, in selection order. */
export function drawablePanels(results: ShipPlotResult[]): Array<{ ship: string; data: PlotResponse }> {
  const out: Array<{ ship: string; data: PlotResponse }> = [];
  for (const r of results) {
    const d = r.data;
    if (!d || (d.plot_type !== 'line' && d.plot_type !== 'scatter')) continue;
    if (!d.x?.length || !d.series?.length) continue;
    out.push({ ship: r.ship, data: d });
  }
  return out;
}

/** Finite min/max across every panel's values, for the shared Y range. */
export function sharedExtent(
  panels: Array<{ data: PlotResponse }>,
  axis: 'left' | 'right',
): [number, number] | null {
  let lo = Infinity;
  let hi = -Infinity;
  for (const { data } of panels) {
    for (const ser of data.series) {
      if (ser.axis !== axis) continue;
      for (const v of ser.values) {
        if (v == null || !Number.isFinite(v)) continue;
        if (v < lo) lo = v;
        if (v > hi) hi = v;
      }
    }
  }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  if (lo === hi) return [lo - 1, hi + 1];
  // A hair of padding so a series does not sit exactly on the frame.
  const pad = (hi - lo) * 0.04;
  return [lo - pad, hi + pad];
}

/** Every ship+signal pair across the grid, so colours match the overlay's. */
function colorMatrix(panels: Array<{ ship: string; data: PlotResponse }>): PlotSeries[] {
  const out: PlotSeries[] = [];
  for (const { ship, data } of panels) {
    for (const ser of data.series) {
      out.push({ ...ser, ship, column: ser.name });
    }
  }
  return out;
}

/**
 * A panel's x + per-series values, ready for uPlot.
 *
 * Line folds repeated x values together (uPlot wants an increasing x) and
 * breaks the path across long gaps. **Scatter does neither**: there, a repeated
 * x is a real measurement - SOG reads 12.4 knots thousands of times - and
 * collapsing them kept only the last point at each value. That is the same bug
 * that ate the overlay's points, and grid still had it: a 200k-point scatter
 * drew in full as an overlay and drew a fraction of that per panel.
 *
 * Gap injection is line-only for the same reason: nothing joins scatter points,
 * so the extra nulls would only pad the arrays.
 */
export function panelSeriesData(
  data: PlotResponse,
  connectGaps: boolean,
  /**
   * Extra per-point values to put through the same transforms - the colour
   * bucket of each point, when the panel is coloured by a column. A line's
   * dedupe and gap injection move points about, so anything indexed by point
   * has to travel with them or it lines up with the wrong ones.
   */
  extra: Array<Array<number | null>> = [],
  /** Live: each line joins its own points across other tables' rows (`bridgeOwnGaps`). */
  bridge = false,
): { x: number[]; series: Array<Array<number | null>>; extra: Array<Array<number | null>> } {
  const xs = data.x.slice();
  const values = [...data.series.map((ser) => ser.values.slice()), ...extra.map((a) => a.slice())];
  const split = (
    result: { x: number[]; series: Array<Array<number | null>> },
  ): { x: number[]; series: Array<Array<number | null>>; extra: Array<Array<number | null>> } => ({
    x: result.x,
    series: result.series.slice(0, data.series.length),
    extra: result.series.slice(data.series.length),
  });
  if (data.plot_type === 'scatter') return split({ x: xs, series: values });
  const sanitized = dedupeByX(xs, values);
  const threshold = connectGaps ? null : computeGapThreshold(sanitized.x);
  const gapped = injectGaps(sanitized.x, sanitized.series, threshold);
  if (bridge && !connectGaps) {
    const n = data.series.length;
    gapped.series.splice(0, n, ...bridgeOwnGaps(gapped.x, gapped.series.slice(0, n)));
  }
  return split(gapped);
}

/** The y=x guide only has meaning for a non-temporal scatter plot. */
export function gridDrawsDiagonal(data: PlotResponse, enabled: boolean): boolean {
  return enabled && data.plot_type === 'scatter' && !data.x_is_time;
}

/**
 * Everything inside a panel that is not the plot area: the title strip, the
 * panel's own padding and border, and uPlot's legend.
 *
 * Measured off a live panel rather than hard-coded, because guessing it is what
 * produced scrollbars - the title was reserved at a flat 26px while the padding,
 * borders and (crucially) the legend were not counted at all. The legend is only
 * measurable after uPlot has built it, which is why sizing happens in two passes.
 */
function panelChrome(): { x: number; y: number } | null {
  const panel = runtime.gridEl?.querySelector<HTMLElement>('.chart-grid-panel.has-plot');
  const inst = runtime.gridInstances[0];
  if (!panel || !inst) return null;

  // Measured part by part rather than as `panel.offsetHeight - inst.height`:
  // the panel clips its content, so once a first-pass canvas is too tall that
  // subtraction reads the clipped box and reports no chrome at all.
  const cs = getComputedStyle(panel);
  const num = (v: string): number => parseFloat(v) || 0;
  const x =
    num(cs.paddingLeft) +
    num(cs.paddingRight) +
    num(cs.borderLeftWidth) +
    num(cs.borderRightWidth);
  let y =
    num(cs.paddingTop) +
    num(cs.paddingBottom) +
    num(cs.borderTopWidth) +
    num(cs.borderBottomWidth);

  const title = panel.querySelector<HTMLElement>('.chart-grid-title');
  if (title) y += Math.ceil(title.getBoundingClientRect().height);

  // uPlot's legend is DOM below the canvas and is not part of the size it is
  // given, so it has to be reserved separately - the same measurement
  // `chartContainerSize` makes for the single chart.
  const legend = inst.root.querySelector<HTMLElement>('.u-legend');
  if (legend && legend.offsetParent !== null) {
    const ls = getComputedStyle(legend);
    y +=
      Math.ceil(legend.getBoundingClientRect().height) +
      Math.ceil(num(ls.marginTop)) +
      Math.ceil(num(ls.marginBottom));
  }
  return { x, y };
}

/**
 * Plot-area size per panel, and the column count the CSS grid must use to match.
 *
 * The two are computed together on purpose: uPlot is told an explicit pixel
 * size, so if CSS chose a different number of columns the canvases would not
 * line up with their boxes. The renderer therefore sets `grid-template-columns`
 * from this same number rather than letting `auto-fit` decide.
 *
 * The height divides the host's *actual* box between the rows. There is no
 * `rows × minimum` floor: that floor is what forced the grid taller than its
 * container and produced a scrollbar. Panels shrink instead.
 */
function panelLayout(
  host: HTMLElement,
  count: number,
  chrome: { x: number; y: number } = { x: 0, y: 0 },
  comparison = false,
): { cols: number; width: number; height: number } {
  const availW = Math.max(MIN_PANEL_WIDTH, host.clientWidth || 600);
  const cols = gridColumns(availW, count, comparison, plotState.value.style.grid_columns);
  const rows = Math.ceil(count / cols);
  const availH = host.clientHeight || 360;
  const width = Math.max(
    40,
    Math.floor((availW - GRID_GAP * (cols - 1)) / cols) - chrome.x,
  );
  const height = comparison
    ? Math.max(240, Math.floor((availH - GRID_GAP * (rows - 1)) / rows) - chrome.y)
    : Math.max(PANEL_MIN_HEIGHT, Math.floor((availH - GRID_GAP * (rows - 1)) / rows) - chrome.y);
  return { cols, width, height };
}

/** Apply the column count the sizing assumed, so canvas and box agree. */
function applyColumns(host: HTMLElement, cols: number): void {
  host.style.gridTemplateColumns = `repeat(${cols}, minmax(0, 1fr))`;
}

/** Redraw every panel at the current container size. */
export function resizeGrid(): void {
  const host = runtime.gridEl;
  if (!host || !runtime.gridInstances.length) return;
  const chrome = panelChrome() ?? { x: 0, y: 0 };
  const comparison = runtime.lastByPeriod !== null;
  const { cols, width, height } = panelLayout(
    host,
    runtime.gridPanelCount || runtime.gridInstances.length,
    chrome,
    comparison,
  );
  applyColumns(host, cols);
  for (const u of runtime.gridInstances) u.setSize({ width, height });
}

/**
 * Grid watches its own host rather than being driven by `resizePlot`, which
 * would make `chart-dom` import this module and close an import cycle. The
 * observer covers window resizes and panel-divider drags alike, since both
 * change the host's box.
 */
function ensureGridResizeObs(host: HTMLElement): void {
  if (runtime.gridResizeObs) return;
  runtime.gridResizeObs = new ResizeObserver(() => resizeGrid());
  runtime.gridResizeObs.observe(host);
}

/** Heatmap responses that carry a grid, in selection order. */
export function heatmapPanels(
  results: ShipPlotResult[],
): Array<{ ship: string; data: HeatmapResponse }> {
  const out: Array<{ ship: string; data: HeatmapResponse }> = [];
  for (const r of results) {
    const d = r.data;
    if (!d || d.plot_type !== 'heatmap') continue;
    if (!d.counts?.length || d.x_edges.length < 2) continue;
    out.push({ ship: r.ship, data: d });
  }
  return out;
}

/** Pie responses that carry slices, in selection order. */
export function piePanels(results: ShipPlotResult[]): Array<{ ship: string; data: PieResponse }> {
  const out: Array<{ ship: string; data: PieResponse }> = [];
  for (const r of results) {
    const d = r.data;
    if (!d || d.plot_type !== 'pie' || !d.slices?.length) continue;
    out.push({ ship: r.ship, data: d });
  }
  return out;
}

/** Sankey responses with rows behind them, in selection order. */
export function sankeyPanels(results: ShipPlotResult[]): Array<{ ship: string; data: SankeyResponse }> {
  const out: Array<{ ship: string; data: SankeyResponse }> = [];
  for (const r of results) {
    const d = r.data;
    if (!d || d.plot_type !== 'sankey' || !d.source_rows) continue;
    out.push({ ship: r.ship, data: d });
  }
  return out;
}

/** Box responses that carry boxes, in selection order. */
export function boxPanels(results: ShipPlotResult[]): Array<{ ship: string; data: BoxResponse }> {
  const out: Array<{ ship: string; data: BoxResponse }> = [];
  for (const r of results) {
    const d = r.data;
    if (!d || d.plot_type !== 'box' || !d.boxes?.length) continue;
    out.push({ ship: r.ship, data: d });
  }
  return out;
}

/** Correlation responses that carry a matrix, in selection order. */
export function correlationPanels(
  results: ShipPlotResult[],
): Array<{ ship: string; data: CorrelationResponse }> {
  const out: Array<{ ship: string; data: CorrelationResponse }> = [];
  for (const r of results) {
    const d = r.data;
    if (!d || d.plot_type !== 'correlation') continue;
    if (!d.n || !d.values.length) continue;
    out.push({ ship: r.ship, data: d });
  }
  return out;
}

/**
 * One cell-matrix chart per ship - a heatmap or a correlation matrix.
 *
 * Shares the panel scaffolding with the series grid (title strip, mount,
 * per-panel tooltip) and takes the drawing itself from the single-chart
 * renderer through `build`, so a panel and the single chart cannot drift apart.
 *
 * Heatmap panels are each binned over their own extent unless axis bounds pin
 * the grid; `doPlot` warns when they end up different, since side-by-side panels
 * imply a cell-for-cell comparison the edges do not support. Correlation panels
 * have no such trap - every matrix is on the same fixed -1..+1 scale - but they
 * can hold different signals if a ship is missing one.
 */
function renderCellGrid<T extends HeatmapResponse | CorrelationResponse | PieResponse | BoxResponse | NumberResponse | SankeyResponse>(
  host: HTMLElement,
  panels: Array<{ ship: string; data: T }>,
  build: (
    data: T,
    c: ThemeColors,
    size: { width: number; height: number },
    target: { el: HTMLElement; container: HTMLElement },
  ) => { opts: uPlot.Options; uData: uPlot.AlignedData },
): void {
  const c = themeColors();
  const { cols, width, height } = panelLayout(host, panels.length);
  ensureGridResizeObs(host);
  applyColumns(host, cols);

  panels.forEach(({ ship, data }) => {
    const panel = document.createElement('div');
    panel.className = 'chart-grid-panel has-plot';
    const title = document.createElement('div');
    title.className = 'chart-grid-title';
    title.textContent = shipLabel(ship);
    if (data.warning) title.title = data.warning;
    panel.appendChild(title);
    const mount = document.createElement('div');
    panel.appendChild(mount);
    const tip = document.createElement('div');
    tip.className = 'uplot-tooltip';
    panel.appendChild(tip);
    host.appendChild(panel);

    const { opts, uData } = build(data, c, { width, height }, {
      el: tip,
      container: panel,
    });
    runtime.inRebuild = true;
    try {
      runtime.gridInstances.push(new uPlot(opts, uData, mount));
    } finally {
      runtime.inRebuild = false;
    }
  });

  resizeGrid();
  chartReady.set(true);
}

export function renderGrid(results: ShipPlotResult[]): void {
  const host = runtime.gridEl;
  if (!host) return;
  // Panel legends and tooltips follow the chart's text size, same as the canvas.
  applyChartFontVar(host);
  const numbers = results.flatMap((result) => result.data?.plot_type === 'number'
    ? [{ ship: result.ship, data: result.data }] : []);
  if (numbers.length) {
    destroyGrid();
    colorLegend.set(null);
    renderCellGrid(host, numbers, (data, c, size) => buildNumberOpts(data, c, size));
    return;
  }
  const heat = heatmapPanels(results);
  if (heat.length) {
    destroyGrid();
    // Each heatmap panel carries its own scale, so the shared strip has nothing
    // to say - and a scatter grid's gradient must not be left over it.
    colorLegend.set(null);
    renderCellGrid(host, heat, (data, c, size, target) =>
      buildHeatmapOpts(data, c, size, target),
    );
    return;
  }
  const corr = correlationPanels(results);
  if (corr.length) {
    destroyGrid();
    // Every correlation panel shares one fixed -1..+1 ramp, so the strip means
    // the same thing over all of them and is set rather than cleared.
    renderCellGrid(host, corr, (data, c, size, target) =>
      buildCorrelationOpts(data, c, size, target),
    );
    correlationLegend(corr[0]!.data);
    return;
  }
  const pies = piePanels(results);
  if (pies.length) {
    destroyGrid();
    pieLegend();
    renderCellGrid(host, pies, (data, c, size, target) => buildPieOpts(data, c, size, target));
    return;
  }
  const flows = sankeyPanels(results);
  if (flows.length) {
    destroyGrid();
    colorLegend.set(null);
    renderCellGrid(host, flows, (data, c, size, target) => buildSankeyOpts(data, c, size, target));
    return;
  }
  const boxes = boxPanels(results);
  if (boxes.length) {
    destroyGrid();
    boxLegend();
    // One Y scale across the panels unless the gear says otherwise: boxes are
    // compared by height, and two panels on different scales invite misreading.
    const shared = plotState.value.style.grid_shared_y
      ? boxYRange(boxes.flatMap((p) => p.data.boxes))
      : undefined;
    renderCellGrid(host, boxes, (data, c, size, target) =>
      buildBoxOpts(data, c, size, target, undefined, shared),
    );
    return;
  }
  const panels = drawablePanels(results);
  destroyGrid();
  if (!panels.length) return;
  runtime.gridPanelCount = panels.length;

  const s = plotState.value;
  const style = s.style;
  const c = themeColors();
  const colorOf = buildSeriesColorer(s, colorMatrix(panels), plotFocusShip.value);
  const sharedLeft = style.grid_shared_y ? sharedExtent(panels, 'left') : null;
  const sharedRight = style.grid_shared_y ? sharedExtent(panels, 'right') : null;
  // Colour-by is unavailable on an overlay because ships already own the colour
  // channel there. In grid each ship has its own panel, so the channel is free -
  // the same reason the leg and changeover bands come back in this view. One
  // plan for the whole grid keeps a value the same colour in every panel.
  const colorScaleName = s.color_scale || 'Viridis';
  const colorPlan = colorPlanFor(panels, colorScaleName);
  const colorLegendBox =
    colorPlan?.kind === 'discrete'
      ? { title: colorPlan.colorBy, items: colorPlan.legend }
      : null;
  // First pass: a size that ignores chrome, purely so uPlot builds its legend
  // and the panel gets a real box to measure. `fitPanels` below corrects it
  // before the browser paints, the same two-step `rebuildUplot` uses.
  const { cols, width, height } = panelLayout(host, panels.length);
  const size = { width, height };
  ensureGridResizeObs(host);
  applyColumns(host, cols);

  // Colour indices are global across the grid so a ship keeps one colour in
  // every panel, matching what the same selection looks like as an overlay.
  let colorIdx = 0;

  panels.forEach(({ ship, data }) => {
    const panel = document.createElement('div');
    panel.className = 'chart-grid-panel has-plot';
    const title = document.createElement('div');
    title.className = 'chart-grid-title';
    title.textContent = shipLabel(ship);
    if (data.warning) title.title = data.warning;
    panel.appendChild(title);
    const mount = document.createElement('div');
    panel.appendChild(mount);
    // One tooltip per panel. The cursor is synced across panels, so hovering
    // any of them should read out every ship at that instant - a single shared
    // node could only ever show one.
    const tip = document.createElement('div');
    tip.className = 'uplot-tooltip';
    panel.appendChild(tip);
    host.appendChild(panel);

    const hasRight = data.series.some((ser) => ser.axis === 'right');
    const isScatter = data.plot_type === 'scatter';
    // This ship's points, in the grid's buckets. A ship whose schema lacks the
    // colour column comes back without `color_values` (the request was scoped
    // for it), so it keeps its series colour while the others are coloured.
    const coloured = !!(colorPlan && data.color_by && data.color_values?.length);
    // Scatter colours each point, so it wants them grouped by bucket; a line
    // colours the segments between points, so it wants a bucket per point, in
    // order, carried through the dedupe and gap injection with the y values.
    const buckets =
      coloured && isScatter ? buildColorBuckets(data.color_values!, colorPlan!, colorScaleName) : null;
    const perPoint = coloured && !isScatter ? pointBuckets(data.color_values!, colorPlan!) : null;
    // Fits share the panel's x, so they ride the same dedupe and gap pipeline
    // as the series they fit, appended after them.
    const fitEntries = data.fits.filter(
      (fit) => !fit.error && Array.isArray(fit.y) && fit.y.length === data.x.length,
    );
    const fitSeries: PlotSeries[] = fitEntries.map((fit) => ({
      name: `${fit.series} · fit`,
      values: fit.y!.slice(),
      axis: data.series.find((ser) => ser.name === fit.series)?.axis ?? 'left',
    }));
    const plotted = panelSeriesData(
      fitSeries.length ? { ...data, series: [...data.series, ...fitSeries] } : data,
      !!style.connect_gaps,
      perPoint ? [perPoint] : [],
      !sourceHasHistory(plottedSource.value),
    );
    const lineBuckets = perPoint ? plotted.extra[0]! : null;
    const lineColors = colorPlan ? planColors(colorPlan, colorScaleName) : [];
    // Point opacity is a scatter setting; the single chart applies it too.
    const alpha = Math.max(0, Math.min(1, style.scatter_alpha ?? 0.7));

    const seriesOpts: uPlot.Series[] = [{}];
    const uData: uPlot.AlignedData = [plotted.x as number[]];
    const seriesScales: string[] = ['x'];
    const seriesColors: string[] = [];
    data.series.forEach((ser, i) => {
      const color = colorOf({ name: ser.name, ship, column: ser.name }, colorIdx++);
      seriesColors.push(color);
      const pointColor = isScatter ? withAlpha(color, alpha) : color;
      const scale = ser.axis === 'right' && hasRight ? 'yR' : 'y';
      const opt: uPlot.Series = {
        label: ser.name,
        stroke: color,
        width: style.line_width,
        scale,
        spanGaps: !!style.connect_gaps,
        points: {
          // uPlot's own points are one colour per series; the hook below draws
          // them bucket by bucket instead, so they must not be drawn twice.
          show: buckets ? false : isScatter || !!style.show_points,
          size: isScatter
            ? Math.max(2, style.scatter_point_size)
            : Math.max(4, style.line_width + 3),
          stroke: pointColor,
          fill: pointColor,
        },
      };
      // Scatter never draws a path; a coloured line draws its own, below.
      if (isScatter || lineBuckets) opt.paths = () => null;
      seriesOpts.push(opt);
      seriesScales.push(scale);
      uData.push(plotted.series[i] as Array<number | null>);
    });
    // Fit overlays: dashed, after the series so they sit on top, in the colour
    // of the series they fit - which in grid is this ship's colour for it.
    fitEntries.forEach((fit, fitIndex) => {
      const baseIndex = data.series.findIndex((ser) => ser.name === fit.series);
      const base = data.series[baseIndex];
      const scale = base?.axis === 'right' && hasRight ? 'yR' : 'y';
      seriesOpts.push({
        label: `${fit.series} · fit`,
        stroke: baseIndex >= 0 ? (s.fits.find((f) => f.series === fit.series)?.color || seriesColors[baseIndex]!) : '#888888',
        width: Math.max(2.25, style.line_width + 1),
        dash: [7, 4],
        scale,
        spanGaps: true,
        points: { show: false },
      });
      seriesScales.push(scale);
      uData.push(plotted.series[data.series.length + fitIndex] as Array<number | null>);
    });

    const scales: Record<string, uPlot.Scale> = {
      x: { time: !!data.x_is_time },
      y: style.log_scale ? { distr: 3 } : {},
    };
    if (sharedLeft) scales.y = { ...scales.y, range: () => sharedLeft };
    if (hasRight) scales.yR = sharedRight ? { range: () => sharedRight } : {};

    const leftLabel = yAxisTitle(data.series, 'left', s.axis_titles.y);
    const rightLabel = yAxisTitle(data.series, 'right', s.axis_titles.yr);
    const axes: uPlot.Axis[] = [
      buildXAxis(c, !!data.x_is_time, data.x_column || (data.x_is_time ? 'Time' : 'x')),
      buildYAxis(c, 'y', 'left', leftLabel),
    ];
    if (hasRight) axes.push(buildYAxis(c, 'yR', 'right', rightLabel));

    const opts: uPlot.Options = {
      ...size,
      scales,
      axes,
      series: seriesOpts,
      legend: buildLegendOpts(seriesOpts.length),
      // One shared cursor key links the crosshair across panels, so hovering a
      // time in one ship marks the same time in the others.
      cursor: {
        sync: { key: 'nde-grid' },
        drag: { x: true, y: false },
      },
      hooks: {
        ...(buckets
          ? {
              drawSeries: [
                (u: uPlot, seriesIndex: number): void => {
                  if (seriesIndex < 1 || seriesIndex > data.series.length) return;
                  drawBucketedScatterSeries(
                    u,
                    seriesIndex,
                    uData,
                    seriesScales[seriesIndex]!,
                    buckets,
                    style.scatter_point_size,
                    style.scatter_alpha,
                  );
                },
              ],
            }
          : {}),
        setCursor: [
          (u: uPlot): void => {
            // Every panel updates its own tooltip, including the ones the
            // cursor reached through sync - that is what makes hovering one
            // panel read out all the ships at once. Each hides itself when its
            // own cursor index goes null, which is what happens to all of them
            // when the pointer leaves the grid.
            updateTooltip(u, data, seriesOpts, undefined, { el: tip, container: panel });
          },
        ],
        // Leg and changeover bands are per-ship, so each panel draws its own.
        // This is exactly what makes these overlays available in grid and not
        // on a shared overlay, where the bands would have no owner.
        draw: [
          (u: uPlot): void => {
            if (lineBuckets) {
              for (let i = 1; i <= data.series.length; i++) {
                drawColoredLineSegments(
                  u,
                  i,
                  uData,
                  seriesScales[i]!,
                  lineBuckets,
                  lineColors,
                  style.line_width,
                  !!style.connect_gaps,
                );
              }
            }
            // A discrete legend belongs on the panel, not under the grid: the
            // swatches are the same in every panel, but so is the chart they
            // explain, and a strip under six panels points at none of them.
            if (colorLegendBox && (buckets || lineBuckets)) drawColorLegendBox(u, colorLegendBox);
            if (gridDrawsDiagonal(data, !!style.show_diagonal)) drawDiagonal(u);
            const st = plotState.value;
            if (st.show_legs) {
              const legs = runtime.legsByShip[ship];
              if (legs?.length) drawLegBands(u, legs);
            }
            if (st.show_changeovers) {
              const cos = runtime.changeoversByShip[ship];
              if (cos?.length) drawChangeoverBands(u, cos);
            }
          },
        ],
        setScale: [
          (u, key): void => {
            // Only the first panel drives the shared date range; letting every
            // panel write it would fire N replots for one drag.
            if (key === 'x' && u === runtime.gridInstances[0]) {
              onXScaleChanged(u, !!data.x_is_time);
            }
          },
        ],
      },
    };
    if (data.x_is_time) opts.tzDate = utcTzDate;
    applyHoverFocus(opts);

    followLiveWindow(opts);
    runtime.inRebuild = true;
    let inst: uPlot;
    try {
      inst = new uPlot(opts, uData, mount);
      pinLiveWindow(inst);
      runtime.gridInstances.push(inst);
    } finally {
      runtime.inRebuild = false;
    }
    // Scoped to this panel: in grid each ship's legend bulk-toggles its own
    // panel. Attached before `resizeGrid`, which measures the legend as chrome.
    attachLegendTools(inst, inst);
  });

  // Second pass: now that a panel exists and uPlot has drawn its legend, the
  // chrome is measurable. Re-size before paint so the corrected layout is the
  // first thing rendered and the grid never overflows its card.
  resizeGrid();
  // The gradient strip is shared - it is one scale for the whole grid, and it
  // reads that way under it. The discrete legend is drawn on each panel instead
  // (above), so it is never both.
  if (colorPlan?.kind === 'numeric') {
    colorLegend.set({
      colorBy: colorPlan.colorBy,
      min: Number(colorPlan.domain.min.toPrecision(4)),
      max: Number(colorPlan.domain.max.toPrecision(4)),
      gradientCss: colorScaleGradientCss(s.color_scale),
    });
  } else {
    colorLegend.set(null);
  }
  chartReady.set(true);
}

interface PeriodSeriesPanel {
  result: PlotPanelResult;
  data: PlotResponse | null;
}

function periodSeriesPanels(results: PlotPanelResult[]): PeriodSeriesPanel[] {
  return results.map((result) => ({
    result,
    data:
      result.data?.plot_type === 'line' || result.data?.plot_type === 'scatter'
        ? result.data
        : null,
  }));
}

function elapsedTick(seconds: number): string {
  if (!Number.isFinite(seconds)) return '';
  const sign = seconds < 0 ? '-' : '';
  const value = Math.abs(seconds);
  if (value >= 3600) return `${sign}${Number((value / 3600).toFixed(1))} h`;
  if (value >= 60) return `${sign}${Number((value / 60).toFixed(1))} min`;
  return `${sign}${Number(value.toFixed(1))} s`;
}

function combinedRange(
  shared: [number, number] | null,
  min: number | null,
  max: number | null,
): uPlot.Scale.Range | undefined {
  if (!shared && min == null && max == null) return undefined;
  return (_u, dataMin, dataMax) => [
    min ?? shared?.[0] ?? dataMin,
    max ?? shared?.[1] ?? dataMax,
  ];
}

function periodTitle(panel: PlotPanelResult['panel'], index: number): HTMLElement {
  const title = document.createElement('div');
  title.className = 'chart-grid-title period-grid-title';

  const main = document.createElement('span');
  main.className = 'period-grid-heading';
  const name = document.createElement('strong');
  const currentLabel = resolveComparisonPeriods().find((period) => period.id === panel.id)?.label;
  name.textContent = `${String.fromCharCode(65 + index)} · ${currentLabel || panel.label || `Period ${String.fromCharCode(65 + index)}`}`;
  const detail = document.createElement('span');
  const start = panel.start.replace('T', ' ').slice(0, 16);
  const end = panel.end.replace('T', ' ').slice(0, 16);
  detail.textContent = `${start} → ${end} · ${formatPeriodDuration(periodDurationSeconds(panel))} · UTC`;
  main.append(name, detail);

  const edit = document.createElement('button');
  edit.className = 'period-panel-edit';
  edit.type = 'button';
  edit.textContent = 'Edit period';
  edit.addEventListener('click', () => {
    document.documentElement.classList.remove('sidebar-collapsed');
    expandedPeriodId.set(panel.id);
    requestAnimationFrame(() => {
      document.getElementById(`period-editor-${panel.id}`)?.scrollIntoView({ block: 'nearest' });
      document.getElementById(`period-start-${panel.id}`)?.focus();
    });
  });
  title.append(main, edit);
  return title;
}

function panelStatus(result: PlotPanelResult): HTMLElement {
  const body = document.createElement('div');
  body.className = `period-panel-status is-${result.status}`;
  if (result.status === 'loading') {
    body.textContent = 'Loading this period…';
  } else if (result.status === 'empty') {
    body.textContent = 'No data in this period.';
  } else {
    const message = document.createElement('span');
    message.textContent = result.error || 'This period could not be plotted.';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'text-btn';
    retry.textContent = 'Retry';
    retry.addEventListener('click', () => {
      runtime.retryPeriodPanel?.(result.panel.id);
    });
    body.append(message, retry);
  }
  return body;
}

/** One panel per fixed period, retaining loading, empty and failed slots. */
export function renderPeriodGrid(results: PlotPanelResult[]): void {
  const host = runtime.gridEl;
  if (!host) return;
  destroyGrid();
  const panels = periodSeriesPanels(results);
  if (!panels.length) return;
  runtime.gridPanelCount = panels.length;

  host.classList.add('period-chart-grid');
  const comparison = periodComparison.value;
  const drawable = panels.filter((panel): panel is PeriodSeriesPanel & { data: PlotResponse } => !!panel.data);
  const s = plotState.value;
  const style = s.style;
  const c = themeColors();
  // A signal keeps one colour in every period. The panel is not part of colour identity.
  const colorBasis = drawable[0]
    ? drawable[0].data.series.map((series) => ({ ...series, ship: results[0]!.panel.ship, column: series.name }))
    : [];
  const colorOf = buildSeriesColorer(s, colorBasis, null);
  const sharedLeft = style.grid_shared_y ? sharedExtent(drawable, 'left') : null;
  const sharedRight = style.grid_shared_y ? sharedExtent(drawable, 'right') : null;
  const numericColor = numericColorDomain(drawable);
  const temporal = drawable.some((panel) => !!panel.data.x_is_time);
  const elapsedMode = temporal && comparison?.timeAxis === 'elapsed';
  let sharedX: [number, number] | null = null;
  if (elapsedMode) {
    const longest = Math.max(...panels.map(({ result }) => periodDurationSeconds(result.panel) ?? 0));
    if (longest > 0) sharedX = [0, longest];
  } else if (!temporal && drawable.length) {
    let min = Infinity;
    let max = -Infinity;
    for (const { data } of drawable) {
      for (const value of data.x) {
        if (!Number.isFinite(value)) continue;
        if (value < min) min = value;
        if (value > max) max = value;
      }
    }
    if (Number.isFinite(min) && Number.isFinite(max)) {
      sharedX = min === max ? [min - 1, max + 1] : [min, max];
    }
  }
  if (sharedX && !temporal) {
    if (s.axis_bounds.x_min != null) sharedX[0] = s.axis_bounds.x_min;
    if (s.axis_bounds.x_max != null) sharedX[1] = s.axis_bounds.x_max;
  }
  if (elapsedMode && runtime.periodViewWindow) {
    sharedX = [runtime.periodViewWindow.min, runtime.periodViewWindow.max];
  }
  const { cols, width, height } = panelLayout(host, panels.length, { x: 0, y: 0 }, true);
  ensureGridResizeObs(host);
  applyColumns(host, cols);

  panels.forEach(({ result, data: rawData }, panelIndex) => {
    const displayLabel = resolveComparisonPeriods().find((period) => period.id === result.panel.id)?.label
      || result.panel.label
      || `Period ${String.fromCharCode(65 + panelIndex)}`;
    const panel = document.createElement('div');
    panel.className = `chart-grid-panel period-grid-panel ${rawData ? 'has-plot' : ''}`;
    panel.appendChild(periodTitle(result.panel, panelIndex));
    host.appendChild(panel);

    if (!rawData || result.status !== 'ready') {
      panel.appendChild(panelStatus(result));
      return;
    }

    const elapsed = !!rawData.x_is_time && comparison?.timeAxis === 'elapsed';
    const origin = utcEpochSeconds(result.panel.start) ?? 0;
    const data: PlotResponse = elapsed
      ? { ...rawData, x: rawData.x.map((x) => x - origin), x_is_time: false }
      : rawData;
    const mount = document.createElement('div');
    panel.appendChild(mount);
    const tip = document.createElement('div');
    tip.className = 'uplot-tooltip';
    panel.appendChild(tip);

    const hasRight = data.series.some((series) => series.axis === 'right');
    const isScatter = data.plot_type === 'scatter';
    const hasNumericColorBy =
      isScatter &&
      numericColor != null &&
      rawData.color_values?.length === rawData.x.length;
    const fitEntries = data.fits.filter(
      (fit) => !fit.error && Array.isArray(fit.y) && fit.y.length === data.x.length,
    );
    const fitSeries: PlotSeries[] = fitEntries.map((fit) => {
      const base = data.series.find((series) => series.name === fit.series);
      return {
        name: `${fit.series} · fit`,
        values: fit.y!.slice(),
        axis: base?.axis ?? 'left',
      };
    });
    const plotted = panelSeriesData(
      fitSeries.length ? { ...data, series: [...data.series, ...fitSeries] } : data,
      !!style.connect_gaps,
      [],
      !sourceHasHistory(plottedSource.value),
    );
    const alpha = Math.max(0, Math.min(1, style.scatter_alpha ?? 0.7));
    const seriesOpts: uPlot.Series[] = [{}];
    const uData: uPlot.AlignedData = [plotted.x as number[]];
    const seriesScales: string[] = ['x'];
    data.series.forEach((series, index) => {
      const color = colorOf({ name: series.name, ship: result.panel.ship, column: series.name }, index);
      const pointColor = isScatter ? withAlpha(color, alpha) : color;
      const scale = series.axis === 'right' && hasRight ? 'yR' : 'y';
      const option: uPlot.Series = {
        label: series.name,
        stroke: color,
        width: style.line_width,
        scale,
        spanGaps: !!style.connect_gaps,
        points: {
          show: hasNumericColorBy ? false : isScatter || !!style.show_points,
          size: isScatter ? Math.max(2, style.scatter_point_size) : Math.max(4, style.line_width + 3),
          stroke: pointColor,
          fill: pointColor,
        },
      };
      if (isScatter) option.paths = () => null;
      seriesOpts.push(option);
      seriesScales.push(scale);
      uData.push(plotted.series[index] as Array<number | null>);
    });
    fitEntries.forEach((fit, fitIndex) => {
      const baseIndex = data.series.findIndex((series) => series.name === fit.series);
      const base = data.series[baseIndex];
      const scale = base?.axis === 'right' && hasRight ? 'yR' : 'y';
      seriesOpts.push({
        label: `${fit.series} · fit`,
        stroke: baseIndex >= 0 ? fitColorFor(s, fit.series, baseIndex) : '#888888',
        width: Math.max(2.25, style.line_width + 1),
        dash: [7, 4],
        scale,
        spanGaps: true,
        points: { show: false },
      });
      seriesScales.push(scale);
      uData.push(plotted.series[data.series.length + fitIndex] as Array<number | null>);
    });

    const logConfig = style.log_scale ? { distr: 3 as const, log: 10 as const } : {};
    const yScale: uPlot.Scale = { ...logConfig };
    const scales: Record<string, uPlot.Scale> = {
      x: sharedX
        ? { time: !!data.x_is_time, range: () => sharedX! }
        : { time: !!data.x_is_time },
      y: yScale,
    };
    const leftRange = combinedRange(sharedLeft, s.axis_bounds.y_min, s.axis_bounds.y_max);
    if (leftRange) yScale.range = leftRange;
    if (hasRight) {
      scales.yR = { ...logConfig };
      const rightRange = combinedRange(sharedRight, s.axis_bounds.yr_min, s.axis_bounds.yr_max);
      if (rightRange) scales.yR.range = rightRange;
    }
    const leftLabel = yAxisTitle(data.series, 'left', s.axis_titles.y);
    const rightLabel = yAxisTitle(data.series, 'right', s.axis_titles.yr);
    const xLabel = elapsed ? 'Elapsed from start' : data.x_column || (data.x_is_time ? 'Time' : 'x');
    const xAxis = buildXAxis(c, !!data.x_is_time, xLabel);
    if (elapsed) xAxis.values = (_u, splits) => splits.map(elapsedTick);
    const axes: uPlot.Axis[] = [
      xAxis,
      buildYAxis(c, 'y', 'left', leftLabel),
    ];
    if (hasRight) axes.push(buildYAxis(c, 'yR', 'right', rightLabel));

    const link = comparison?.linkNavigation && (elapsed || !rawData.x_is_time);
    const numericBuckets = hasNumericColorBy
      ? buildColorBuckets(rawData.color_values!, { kind: 'numeric', colorBy: numericColor!.colorBy, domain: numericColor! }, s.color_scale || 'Viridis')
      : null;
    const thresholds = style.show_thresholds ? collectThresholds(rawData) : [];
    const periodEndEpoch = elapsed ? utcEpochSeconds(result.panel.end) : null;
    const opts: uPlot.Options = {
      width,
      height,
      scales,
      axes,
      series: seriesOpts,
      legend: buildLegendOpts(seriesOpts.length),
      cursor: {
        ...(link ? { sync: { key: `nde-period-${runtime.comparisonGeneration}` } } : {}),
        drag: { x: true, y: false },
      },
      hooks: {
        ...(numericBuckets
          ? {
              drawSeries: [
                (u: uPlot, seriesIndex: number): void => {
                  if (seriesIndex < 1 || seriesIndex > data.series.length) return;
                  drawBucketedScatterSeries(
                    u,
                    seriesIndex,
                    uData,
                    seriesScales[seriesIndex]!,
                    numericBuckets,
                    style.scatter_point_size,
                    style.scatter_alpha,
                  );
                },
              ],
            }
          : {}),
        setCursor: [
          (u: uPlot): void => updateTooltip(u, data, seriesOpts, {
            periodLabel: `${String.fromCharCode(65 + panelIndex)} · ${displayLabel}`,
            ...(elapsed ? { periodStartEpoch: origin } : {}),
            ...(periodEndEpoch != null ? { periodEndEpoch } : {}),
            ...(runtime.legsByPanel[result.panel.id]
              ? { periodLegs: runtime.legsByPanel[result.panel.id] }
              : {}),
            ...(runtime.changeoversByPanel[result.panel.id]
              ? { periodChangeovers: runtime.changeoversByPanel[result.panel.id] }
              : {}),
          }, { el: tip, container: panel }),
        ],
        draw: [
          (u: uPlot): void => {
            if (elapsed && sharedX) {
              const duration = periodDurationSeconds(result.panel) ?? 0;
              if (duration < sharedX[1]) {
                const x0 = u.valToPos(duration, 'x', true);
                const x1 = u.valToPos(sharedX[1], 'x', true);
                u.ctx.save();
                u.ctx.fillStyle = 'rgba(128, 128, 128, 0.10)';
                u.ctx.fillRect(x0, u.bbox.top, Math.max(0, x1 - x0), u.bbox.height);
                const ratio = window.devicePixelRatio || 1;
                if (x1 - x0 > 110 * ratio) {
                  u.ctx.fillStyle = c.text;
                  u.ctx.globalAlpha = 0.65;
                  u.ctx.font = `${11 * ratio}px system-ui, sans-serif`;
                  u.ctx.fillText('Outside selected period', x0 + 8 * ratio, u.bbox.top + 18 * ratio);
                }
                u.ctx.restore();
              }
            }
            const current = plotState.value;
            const shift = (value: string | null): string | null => {
              if (!elapsed || !value) return value;
              const seconds = utcEpochSeconds(value);
              return seconds == null ? value : new Date((seconds - origin) * 1000).toISOString();
            };
            if (current.show_legs) {
              const legs = runtime.legsByPanel[result.panel.id]?.map((leg) => ({
                ...leg,
                start: shift(leg.start),
                end: shift(leg.end),
              }));
              if (legs?.length) drawLegBands(u, legs);
            }
            if (current.show_changeovers) {
              const changeovers = runtime.changeoversByPanel[result.panel.id]?.map((changeover) => ({
                ...changeover,
                start: shift(changeover.start),
                end: shift(changeover.end),
              }));
              if (changeovers?.length) drawChangeoverBands(u, changeovers);
            }
            if (thresholds.length) drawThresholds(u, thresholds);
          },
        ],
        setScale: [
          (u, key): void => {
            if (key === 'x') onPeriodXScaleChanged(u, result.panel, elapsed, !!rawData.x_is_time);
          },
        ],
      },
    };
    if (data.x_is_time) opts.tzDate = utcTzDate;
    applyHoverFocus(opts);
    runtime.inRebuild = true;
    try {
      const instance = new uPlot(opts, uData, mount);
      runtime.gridInstances.push(instance);
      attachLegendTools(instance, instance);
    } finally {
      runtime.inRebuild = false;
    }
  });

  resizeGrid();
  if (numericColor) {
    colorLegend.set({
      colorBy: numericColor.colorBy,
      min: Number(numericColor.min.toPrecision(4)),
      max: Number(numericColor.max.toPrecision(4)),
      gradientCss: colorScaleGradientCss(s.color_scale),
    });
  } else {
    colorLegend.set(null);
  }
  chartReady.set(true);
}
